/**
 * Aba "Configuração" (gestor): desenhos de eixos, de-para das posições da
 * Finatto e o ponto de partida (montagens herdadas do snapshot). Tudo
 * auditável; a importação tem simulação e nunca mexe em pneu já gerido.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TireBootstrapReportDto, TireConfigurationListDto, TirePositionMappingDto } from "@bi/types";
import { api, ApiError } from "../../../lib/api";
import { fmtDate } from "../tire-ui";

const KIND: Record<string, string> = { tractor: "Cavalo", truck: "Truck", trailer: "Carreta", light: "Leve", equipment: "Equipamento", other: "Outro" };
const AXLE: Record<string, string> = { steering: "Direcional", traction: "Tração", free: "Livre", lift: "Auxiliar" };

export function TireSetupTab() {
  return (
    <div className="space-y-6">
      <Configurations />
      <Bootstrap />
      <Mappings />
    </div>
  );
}

function Configurations() {
  const qc = useQueryClient();
  const [criando, setCriando] = useState(false);
  const q = useQuery({ queryKey: ["tires", "configurations"], queryFn: () => api.get<TireConfigurationListDto>("/fleet/tire-configurations") });
  const ativar = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => api.patch(`/fleet/tire-configurations/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tires"] }),
  });
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Configurações de eixos</p>
          <p className="text-xs text-gray-500">Cada veículo usa uma. O desenho fica travado depois que um pneu é montado nele. Para mudar, crie outra.</p>
        </div>
        <button className="btn-secondary" onClick={() => setCriando((v) => !v)}>{criando ? "Fechar" : "Nova configuração"}</button>
      </div>
      {criando && <NewConfiguration onDone={() => setCriando(false)} />}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
            <tr>
              <th className="text-left px-4 py-2">Configuração</th>
              <th className="text-left px-4 py-2">Tipo</th>
              <th className="text-left px-4 py-2">Eixos</th>
              <th className="text-right px-4 py-2">Posições</th>
              <th className="text-right px-4 py-2">Veículos</th>
              <th className="text-left px-4 py-2">Situação</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(q.data?.items ?? []).map((c) => (
              <tr key={c.id}>
                <td className="px-4 py-3"><p className="text-gray-900">{c.name}</p><p className="text-xs text-gray-400 font-mono">{c.code}</p></td>
                <td className="px-4 py-3 text-gray-600">{KIND[c.vehicleKind] ?? c.vehicleKind}</td>
                <td className="px-4 py-3 text-gray-600 text-xs">
                  {c.axles.filter((a) => a.axleType !== "spare").map((a) => `${AXLE[a.axleType] ?? a.axleType}${a.dualWheel ? " (duplo)" : ""}`).join(" · ")}
                </td>
                <td className="px-4 py-3 text-right">{c.positionCount}</td>
                <td className="px-4 py-3 text-right">{c.vehicleCount}</td>
                <td className="px-4 py-3">
                  <span className={c.active ? "badge-green" : "badge-gray"}>{c.active ? "Ativa" : "Inativa"}</span>
                  {c.locked && <span className="badge-gray ml-1" title="Já tem pneu montado em alguma posição">Travada</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button className="btn-ghost text-xs" onClick={() => ativar.mutate({ id: c.id, active: !c.active })}>{c.active ? "Desativar" : "Ativar"}</button>
                </td>
              </tr>
            ))}
            {(q.data?.templates ?? []).map((t) => (
              <tr key={t.code} className="text-gray-400">
                <td className="px-4 py-3"><p>{t.name}</p><p className="text-xs font-mono">{t.code}</p></td>
                <td className="px-4 py-3">{KIND[t.vehicleKind] ?? t.vehicleKind}</td>
                <td className="px-4 py-3 text-xs">{t.axleCount} eixo(s) · {t.spareCount} estepe(s)</td>
                <td className="px-4 py-3" colSpan={4}><span className="text-xs">Modelo padrão, criado quando for usado num veículo.</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewConfiguration({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("tractor");
  const [spares, setSpares] = useState(1);
  const [axles, setAxles] = useState<Array<{ axleType: string; dualWheel: boolean }>>([
    { axleType: "steering", dualWheel: false }, { axleType: "traction", dualWheel: true },
  ]);
  const [erro, setErro] = useState<string | null>(null);
  const criar = useMutation({
    mutationFn: () => api.post("/fleet/tire-configurations", { name: name.trim(), code: code.trim(), vehicleKind: kind, spareCount: spares, axles }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tires"] }); onDone(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível criar."),
  });
  return (
    <div className="px-5 py-4 border-b border-surface-border bg-surface-muted/50 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block md:col-span-2"><span className="text-xs text-gray-500">Nome *</span>
          <input className="input w-full mt-0.5" value={name} onChange={(e) => { setName(e.target.value); if (!code) setCode(""); }} placeholder="Ex.: Bitrem dianteiro 2 eixos" /></label>
        <label className="block"><span className="text-xs text-gray-500">Código *</span>
          <input className="input w-full mt-0.5 font-mono" value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="bitrem-dianteiro" /></label>
        <label className="block"><span className="text-xs text-gray-500">Tipo</span>
          <select className="input w-full mt-0.5" value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(KIND).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
      </div>
      <div className="space-y-2">
        {axles.map((a, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 w-16">Eixo {i + 1}</span>
            <select className="input w-40" value={a.axleType} onChange={(e) => setAxles(axles.map((x, j) => (j === i ? { ...x, axleType: e.target.value } : x)))}>
              {Object.entries(AXLE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={a.dualWheel} onChange={(e) => setAxles(axles.map((x, j) => (j === i ? { ...x, dualWheel: e.target.checked } : x)))} />
              Rodado duplo
            </label>
            {axles.length > 1 && <button className="btn-ghost text-xs" onClick={() => setAxles(axles.filter((_, j) => j !== i))}>Remover</button>}
          </div>
        ))}
        <div className="flex items-center gap-3">
          {axles.length < 9 && <button className="btn-ghost text-xs" onClick={() => setAxles([...axles, { axleType: "free", dualWheel: true }])}>+ Eixo</button>}
          <label className="text-sm text-gray-600 inline-flex items-center gap-2">Estepes
            <select className="input w-20" value={spares} onChange={(e) => setSpares(Number(e.target.value))}>
              {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        </div>
      </div>
      {erro && <p className="text-sm text-danger-600">{erro}</p>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={name.trim().length < 2 || code.length < 2 || criar.isPending} onClick={() => { setErro(null); criar.mutate(); }}>
          {criar.isPending ? "Criando…" : "Criar configuração"}
        </button>
      </div>
    </div>
  );
}

function Bootstrap() {
  const qc = useQueryClient();
  const [rel, setRel] = useState<TireBootstrapReportDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const rodar = useMutation({
    mutationFn: (dryRun: boolean) => api.post<TireBootstrapReportDto>("/fleet/tires/bootstrap", { dryRun, assignConfigurations: true }),
    onSuccess: (r) => { setRel(r); if (!r.dryRun) qc.invalidateQueries({ queryKey: ["tires"] }); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível executar."),
  });
  const skip = rel?.skipped;
  return (
    <div className="card p-5 space-y-3">
      <div>
        <p className="text-sm font-semibold text-gray-900">Ponto de partida a partir da Finatto</p>
        <p className="text-xs text-gray-500 max-w-3xl">
          Usa o retrato atual da Finatto (placa + posição de cada pneu montado) para criar as montagens no SIAG. Só monta o que dá para
          posicionar com segurança. O resto fica listado com o motivo. Pode rodar de novo: não duplica e não mexe em pneu que o SIAG já controla.
          Veículos sem configuração recebem a que a Finatto descreve, quando ela é entendida.
        </p>
      </div>
      <div className="flex gap-2">
        <button className="btn-secondary" disabled={rodar.isPending} onClick={() => { setErro(null); rodar.mutate(true); }}>Simular</button>
        <button className="btn-primary" disabled={rodar.isPending || !rel?.dryRun} title={!rel?.dryRun ? "Simule antes" : undefined}
          onClick={() => { setErro(null); rodar.mutate(false); }}>
          {rodar.isPending ? "Processando…" : "Importar montagens"}
        </button>
      </div>
      {erro && <p className="text-sm text-danger-600">{erro}</p>}
      {rel && skip && (
        <div className="rounded-lg bg-surface-muted p-3 text-sm space-y-2">
          <p className="font-medium text-gray-900">{rel.dryRun ? "Simulação" : "Importação concluída"}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="badge-green">{rel.installationsCreated} montagem(ns) {rel.dryRun ? "a criar" : "criada(s)"}</span>
            <span className="badge-blue">{rel.vehiclesConfigured} veículo(s) {rel.dryRun ? "a configurar" : "configurado(s)"}</span>
            {rel.alreadyManaged > 0 && <span className="badge-gray">{rel.alreadyManaged} já gerido(s) pelo SIAG</span>}
            {skip.unmappedPosition > 0 && <span className="badge-amber">{skip.unmappedPosition} posição(ões) não mapeada(s)</span>}
            {skip.noConfiguration > 0 && <span className="badge-amber">{skip.noConfiguration} sem configuração</span>}
            {skip.noVehicle > 0 && <span className="badge-amber">{skip.noVehicle} placa(s) fora do cadastro</span>}
            {skip.positionTaken + skip.duplicateInPosition > 0 && <span className="badge-amber">{skip.positionTaken + skip.duplicateInPosition} conflito(s) de posição</span>}
          </div>
          {rel.unmapped.length > 0 && (
            <details>
              <summary className="cursor-pointer text-xs text-gray-600">Ver os {rel.unmapped.length} pneu(s) não posicionados</summary>
              <div className="mt-2 max-h-64 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="text-gray-500"><tr><th className="text-left py-1">Pneu</th><th className="text-left">Placa</th><th className="text-left">Posição Finatto</th><th className="text-left">Motivo</th></tr></thead>
                  <tbody>
                    {rel.unmapped.map((u) => (
                      <tr key={u.tireId} className="border-t border-surface-border">
                        <td className="py-1 font-mono">{u.code}</td><td className="font-mono">{u.plate}</td>
                        <td>{u.externalLabel ?? (u.externalPosition != null ? `#${u.externalPosition}` : "—")}</td><td className="text-gray-600">{u.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Mappings() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["tires", "mappings"], queryFn: () => api.get<{ items: TirePositionMappingDto[] }>("/fleet/tire-position-mappings") });
  const [editando, setEditando] = useState<Record<string, string>>({});
  const salvar = useMutation({
    mutationFn: ({ id, code }: { id: string; code: string | null }) => api.patch(`/fleet/tire-position-mappings/${id}`, { positionCode: code }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tires", "mappings"] }); setEditando({}); },
  });
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-surface-border">
        <p className="text-sm font-semibold text-gray-900">De-para de posições (Finatto → SIAG)</p>
        <p className="text-xs text-gray-500 max-w-3xl">
          Por padrão a posição é lida da descrição da Finatto, de acordo com o desenho de cada veículo (ex.: “TRACAO DIREITO EXTERNO” = eixo de tração, externo direito).
          Preencha um código só para corrigir um caso que a regra não entende. Vale para todos os veículos que tenham essa posição.
        </p>
      </div>
      {(q.data?.items ?? []).length === 0 ? (
        <p className="p-5 text-sm text-gray-500">Nenhuma posição da Finatto registrada ainda. Ela aparece depois do próximo sync de posições ou da simulação acima.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-muted text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-4 py-2">Cód. Finatto</th>
                <th className="text-left px-4 py-2">Descrição na Finatto</th>
                <th className="text-right px-4 py-2">Pneus montados</th>
                <th className="text-left px-4 py-2">Posição no SIAG</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Atualizado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {q.data!.items.map((m) => (
                <tr key={m.id}>
                  <td className="px-4 py-2.5 font-mono text-gray-600">{m.externalPositionCode}</td>
                  <td className="px-4 py-2.5 text-gray-800">{m.externalLabel ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right">{m.mountedTires}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <input className="input w-36 font-mono uppercase py-1.5" placeholder="automático"
                        value={editando[m.id] ?? m.positionCode ?? ""} onChange={(e) => setEditando({ ...editando, [m.id]: e.target.value.toUpperCase() })} />
                      {editando[m.id] !== undefined && (
                        <button className="btn-ghost text-xs" onClick={() => salvar.mutate({ id: m.id, code: editando[m.id]!.trim() || null })}>Salvar</button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400 hidden md:table-cell">{fmtDate(m.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {salvar.isError && <p className="px-5 pb-3 text-sm text-danger-600">{salvar.error instanceof ApiError ? salvar.error.message : "Não foi possível salvar."}</p>}
    </div>
  );
}
